/**
 * One date formatter, one month formatter. A second implementation is two
 * conventions waiting to disagree, and in a finance app the disagreement is
 * about which month a figure belongs to.
 */

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** `2026-07` → `July 2026`. */
export function formatMonth(month: string): string {
  const [year, index] = month.split("-");
  const name = MONTHS[Number(index) - 1];
  return name && year ? `${name} ${year}` : month;
}

/** `2026-07` → `Jul`, for a picker where the year is already on screen. */
export function formatMonthShort(month: string): string {
  const [year, index] = month.split("-");
  const name = MONTHS[Number(index) - 1];
  return name && year ? `${name.slice(0, 3)} ${year.slice(2)}` : month;
}

/**
 * "March to July 2026", "July 2026", "all time" — the same rendering the
 * server's own `periodLabel` gives a `ResolvedPeriod`, so a heading never says
 * something different from what the figures under it were scoped to.
 */
export function formatPeriod(period: { from: string | null; to: string | null }): string {
  if (!period.from || !period.to) return "All time";
  if (period.from === period.to) return formatMonth(period.from);

  const [fromYear, fromIndex] = period.from.split("-");
  const [toYear] = period.to.split("-");
  const fromName = MONTHS[Number(fromIndex) - 1];

  // The year is said once when both ends share it: "March to July 2026".
  if (fromYear === toYear) return `${fromName ?? ""} to ${formatMonth(period.to)}`;
  return `${formatMonth(period.from)} to ${formatMonth(period.to)}`;
}

/** `2026-07-03` → `3 Jul`. Dates are parsed as text: no timezone can shift them. */
export function formatDay(date: string): string {
  const [, month, day] = date.split("-");
  const name = MONTHS[Number(month) - 1];
  return name && day ? `${Number(day)} ${name.slice(0, 3)}` : date;
}

export function formatFullDate(date: string): string {
  const [year, month, day] = date.split("-");
  const name = MONTHS[Number(month) - 1];
  return name && day && year ? `${Number(day)} ${name} ${year}` : date;
}

export function currentMonth(now: Date = new Date()): string {
  return now.toISOString().slice(0, 7);
}

/** `2026-07` → `2026-06`. */
export function previousMonth(month: string): string {
  const [year, index] = month.split("-").map(Number) as [number, number];
  const total = year * 12 + (index - 1) - 1;
  return `${String(Math.floor(total / 12)).padStart(4, "0")}-${String((total % 12) + 1).padStart(2, "0")}`;
}

/**
 * The month a screen opens on. Statements arrive after the month they cover,
 * so the finished month is the one with anything to look at; the current month
 * stays in the picker for anyone who wants it.
 */
export function defaultMonth(now: Date = new Date()): string {
  return previousMonth(currentMonth(now));
}

/** A timestamp as "3 July 2026", which is all anyone reads it for. */
export function formatTimestamp(iso: string): string {
  return formatFullDate(iso.slice(0, 10));
}

export function pluralise(count: number, one: string, many = `${one}s`): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}
